import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ProjectListBloc } from '@connect-services/client/blocs/project-list.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import {
    Project,
    ProjectControllerGetRequestParams,
    ProjectsService as SdkProjectsService,
    UpdateProjectDto,
} from '@connect-services/client/sdk/api';
import { FetchAllService } from '@connect-services/client/services/fetch-all.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { ProjectCreationEditionForm } from '@connect-services/client/services/project/shaping/shaping-project.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { OrderEnum } from '@shared/client/models/pagination.model';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export type ProjectionOption = {
    text: string;
    value: Project.ProjectionEnum;
};

@Injectable({
    providedIn: 'root',
})
export class ProjectService {
    public readonly projects: WritableSignal<Project[]> = signal([]);

    private readonly fetchAllService = inject(FetchAllService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly matDialog = inject(MatDialog);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectListBloc = inject(ProjectListBloc);
    private readonly projectsService = inject(SdkProjectsService);
    private readonly router = inject(Router);
    private readonly securityService = inject(SecurityService);
    private readonly snackBarService = inject(SnackBarService);
    private readonly translocoService = inject(TranslocoService);

    public async createProject(projectForm: ProjectCreationEditionForm): Promise<string> {
        const project = await firstValueFrom(
            this.projectsService.projectControllerPost({
                createProjectDto: {
                    bbox: projectForm.controls.bbox.value ?? undefined,
                    color: projectForm.controls.color.value,
                    name: projectForm.controls.name.value,
                    projection: projectForm.controls.projection.value,
                },
            }),
        );

        this.projects.set(await this.getProjects());

        return project.id;
    }

    public async deleteProject(project: Project): Promise<void> {
        try {
            await firstValueFrom(
                this.projectsService.projectControllerDelete({
                    id: project.id,
                }),
            );

            setTimeout(() => this.openSuccessfulDeleteModal(project.name), 1000);
            await this.refreshProjectListBloc();
            await this.router.navigate([RoutePathEnum.PROJECTS]);
        } catch {
            this.snackBarService.openErrorSnackBar('errorDELETE');
        }
    }

    public getProjectionSelection(): ProjectionOption[] {
        return Object.values(Project.ProjectionEnum).map((projectionCode) => ({
            text: this.getProjectionText(projectionCode),
            value: projectionCode,
        }));
    }

    public getProjectionText(projection: string): string {
        const projectionLabels: Record<string, string> = {
            '2154': 'Lambert 93',
            '2972': 'UTM 22 N',
            '2975': 'UTM 40 S',
            '3943': 'CC 43',
            '3944': 'CC 44',
            '3945': 'CC 45',
            '3946': 'CC 46',
            '3947': 'CC 47',
            '3948': 'CC 48',
            '3949': 'CC 49',
            '3950': 'CC 50',
            '4326': 'WGS 84',
            '4471': 'UTM 38 S',
            '32620': 'UTM 20 N',
        };

        if (!Object.hasOwn(projectionLabels, projection)) {
            return projection;
        }

        return projectionLabels[projection];
    }

    public getProjects(search?: string): Promise<Project[]> {
        return this.fetchAllService.getAll<ProjectControllerGetRequestParams, Project>(
            this.projectsService.projectControllerGet.bind(this.projectsService),
            {
                order: OrderEnum.ASC,
                orderBy: 'name',
                person: this.securityService.meSignal()!.id,
                properties: ['name'],
                value: search,
            },
        );
    }

    public async updateProject(projectCreationEditionForm: ProjectCreationEditionForm): Promise<void> {
        const shouldRefreshSources = projectCreationEditionForm.controls.bbox.dirty;

        const updateProjectDto: UpdateProjectDto = {
            ...projectCreationEditionForm.value,
        };

        await firstValueFrom(
            this.projectsService.projectControllerPatch({
                id: this.projectBloc.value()!.id,
                updateProjectDto,
            }),
        );

        this.projects.set(await this.getProjects());

        await this.refreshProject(shouldRefreshSources);
    }

    private openSuccessfulDeleteModal(projectName: string): void {
        this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                confirmLabel: this.translocoService.translate('close'),
                title: this.translocoService.translate('projectValueHasBeenSuccessfullyDeleted', {
                    projectName: projectName,
                }),
                type: ModalType.SUCCESS,
            },
            maxWidth: '30vw',
        });
    }

    private async refreshProject(areRefreshSourcesNeeded?: boolean): Promise<void> {
        await this.refreshProjectListBloc();

        await this.projectBloc.load(
            {
                id: this.projectBloc.value()!.id,
            },
            { refresh: true },
        );

        if (!areRefreshSourcesNeeded) {
            return;
        }

        await this.mapSourceService.redrawSources();
    }

    private async refreshProjectListBloc(): Promise<void> {
        if (!this.securityService.meSignal()!.organization) {
            return;
        }

        await this.projectListBloc.load(
            {
                orderBy: 'name',
                person: this.securityService.meSignal()!.id,
            },
            { refresh: true },
        );
    }
}
